import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const STORAGE_KEY = "storefront_settings_cache_v1";

// Try to hydrate from localStorage so we don't flash a hardcoded default
// (e.g. "Maison") before the API responds.
function loadCached() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") return parsed;
    } catch { /* ignore */ }
    return null;
}

// First-ever visit → empty string for store_name (renders nothing) instead of
// a hardcoded fallback that would later "flash" to the real store name.
const EMPTY_SETTINGS = { currency_symbol: "₹", currency_code: "INR", store_name: "" };

const SettingsContext = createContext(EMPTY_SETTINGS);

export function SettingsProvider({ children }) {
    const [settings, setSettings] = useState(() => loadCached() || EMPTY_SETTINGS);

    const refresh = async () => {
        try {
            const { data } = await api.get("/settings");
            setSettings(data);
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
        } catch { /* keep last known */ }
    };

    useEffect(() => { refresh(); }, []);

    // Keep the browser tab title in sync with the store name from Settings.
    useEffect(() => {
        const name = (settings && settings.store_name) ? String(settings.store_name).trim() : "";
        if (name) {
            document.title = name;
            const meta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
            if (meta) meta.setAttribute("content", name);
        }
    }, [settings]);

    return (
        <SettingsContext.Provider value={{ ...settings, refresh, setSettings }}>
            {children}
        </SettingsContext.Provider>
    );
}

export function useSettings() { return useContext(SettingsContext); }

export function formatMoney(symbol, amount) {
    return symbol + Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function effectivePrice(p) {
    const d = Number(p?.discount_percent || 0);
    const base = Number(p?.price || 0);
    return d > 0 ? Math.round(base * (1 - d / 100) * 100) / 100 : base;
}
